import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TileCard from './TileCard';
import {
  getCachedResponse,
  getWorkflowRunsBreakdown,
  getWorkflowRunsByName,
  postResubmitLogicAppRun,
} from '../api/client';
import type {
  Env,
  WorkflowRunSummary,
  WorkflowRunsBreakdown,
  WorkflowRunsByName,
} from '../types';

const HELP =
  'Operational actions for individual Logic App runs. Pick a workflow, inspect recent ' +
  'runs, and resubmit a Failed or Terminated run straight to the hostruntime — a fresh ' +
  'run is queued with the same trigger inputs. Every resubmit is audited to App Insights ' +
  'with the authenticated user.';

const STATUS_OPTIONS = ['Failed', 'Terminated', 'All'] as const;
type StatusFilter = (typeof STATUS_OPTIONS)[number];

interface Props {
  env: Env;
  refreshToken?: number;
  onResubmitted?: () => void;
}

interface RowState {
  running: boolean;
  result: string | null;
  error: string | null;
}

function formatTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleTimeString();
}

function pillClass(status: string): string {
  const s = status.toLowerCase();
  if (s === 'succeeded') return 'pill pill-succeeded';
  if (s === 'running') return 'pill';
  if (s === 'cancelled') return 'pill';
  return 'pill pill-failed';
}

function isResubmittable(run: WorkflowRunSummary): boolean {
  const s = run.status.toLowerCase();
  return s === 'failed' || s === 'terminated' || s === 'cancelled';
}

export default function WorkflowRunActionsPanel({ env, refreshToken = 0, onResubmitted }: Props) {
  const navigate = useNavigate();
  const breakdown = getCachedResponse<WorkflowRunsBreakdown>('workflow-runs-breakdown', {
    env,
    windowHours: 24,
  });
  const [workflows, setWorkflows] = useState<string[]>(() => workflowNames(breakdown));
  const [selected, setSelected] = useState<string>(() => pickDefaultWorkflow(breakdown));
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('Failed');
  const [runs, setRuns] = useState<WorkflowRunsByName | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rowState, setRowState] = useState<Record<string, RowState>>({});

  // Refresh the workflow list whenever the breakdown data is refreshed externally.
  useEffect(() => {
    let cancelled = false;
    getWorkflowRunsBreakdown(env, 24)
      .then((d) => {
        if (cancelled) return;
        const names = workflowNames(d);
        setWorkflows(names);
        if (!selected && names.length > 0) setSelected(pickDefaultWorkflow(d));
      })
      .catch(() => {
        /* the breakdown tile already surfaces errors */
      });
    return () => {
      cancelled = true;
    };
  }, [env, refreshToken]);

  // Fetch runs whenever workflow or filter changes.
  useEffect(() => {
    if (!selected) {
      setRuns(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setRowState({});
    const statusArg = statusFilter === 'All' ? undefined : statusFilter;
    getWorkflowRunsByName(env, selected, 20, statusArg)
      .then((d) => {
        if (!cancelled) setRuns(d);
      })
      .catch((e: Error) => {
        if (!cancelled) setError(e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [env, selected, statusFilter, refreshToken]);

  function updateRow(runId: string, patch: Partial<RowState>) {
    setRowState((s) => {
      const existing: RowState = s[runId] ?? { running: false, result: null, error: null };
      return { ...s, [runId]: { ...existing, ...patch } };
    });
  }

  async function resubmit(run: WorkflowRunSummary) {
    const confirmed = window.confirm(
      `Resubmit ${selected} / ${run.name}?\n\n` +
        `This queues a fresh run with the same trigger inputs. The original run stays ` +
        `visible in history. Every resubmit is audited with your UPN.`,
    );
    if (!confirmed) return;
    updateRow(run.name, { running: true, result: null, error: null });
    try {
      const res = await postResubmitLogicAppRun({
        env,
        workflowName: selected,
        runId: run.name,
      });
      const msg = res.newRunId ? `New run ${res.newRunId}` : 'Queued (new run ID not returned)';
      updateRow(run.name, { running: false, result: msg });
      onResubmitted?.();
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Unknown error';
      updateRow(run.name, { running: false, error: message });
    }
  }

  const footer = runs ? (
    <span>
      As of {new Date(runs.generatedAt).toLocaleTimeString()} · {runs.logicAppName} · top 20
    </span>
  ) : undefined;

  const resubmittableCount = useMemo(
    () => runs?.runs.filter(isResubmittable).length ?? 0,
    [runs],
  );

  return (
    <TileCard
      title={`Workflow run actions — ${env.toUpperCase()}`}
      help={HELP}
      loading={loading}
      error={error}
      footer={footer}
    >
      <div className="wfrun-actions-controls">
        <label className="wfrun-label">
          Workflow
          <select
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            className="wfrun-select"
            disabled={workflows.length === 0}
          >
            {workflows.length === 0 && <option value="">(no workflows)</option>}
            {workflows.map((w) => (
              <option key={w} value={w}>
                {w}
              </option>
            ))}
          </select>
        </label>
        <label className="wfrun-label">
          Status
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            className="wfrun-select"
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>
      </div>

      {!selected && <p className="tile-empty">Pick a workflow to list recent runs.</p>}
      {selected && !loading && runs && runs.runs.length === 0 && (
        <p className="tile-empty">
          No {statusFilter === 'All' ? '' : statusFilter.toLowerCase() + ' '}runs for {selected} in
          the last window.
        </p>
      )}
      {selected && runs && runs.runs.length > 0 && (
        <div className="workflow-table-wrap">
          <table className="workflow-table">
            <thead>
              <tr>
                <th>Run</th>
                <th>Status</th>
                <th>Started</th>
                <th>Ended</th>
                <th>Trace</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {runs.runs.map((r) => {
                const st = rowState[r.name] ?? { running: false, result: null, error: null };
                const canResubmit = isResubmittable(r);
                const traceId = r.correlationId ?? r.name;
                return (
                  <tr key={r.name}>
                    <td className="wfrun-id" title={r.correlationId ?? ''}>
                      {r.name.slice(0, 12)}…
                    </td>
                    <td>
                      <span className={pillClass(r.status)}>{r.status}</span>
                      {r.code && <span className="muted"> ({r.code})</span>}
                    </td>
                    <td>{formatTime(r.startTime)}</td>
                    <td>{formatTime(r.endTime)}</td>
                    <td>
                      <button
                        type="button"
                        onClick={() =>
                          navigate(`/${env}/app-insights?correlationId=${encodeURIComponent(traceId)}`)
                        }
                        className="trace-drill-btn"
                        title="Open correlation trace in App Insights drill-down"
                      >
                        Trace →
                      </button>
                    </td>
                    <td>
                      {canResubmit ? (
                        <>
                          <button
                            type="button"
                            onClick={() => resubmit(r)}
                            disabled={st.running}
                            className="dlq-resubmit-btn"
                          >
                            {st.running ? 'Resubmitting…' : 'Resubmit'}
                          </button>
                          {st.result && <div className="dlq-result-ok">{st.result}</div>}
                          {st.error && <div className="dlq-result-err">Failed: {st.error}</div>}
                        </>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {resubmittableCount > 0 && statusFilter === 'All' && (
            <p className="muted wfrun-hint">
              {resubmittableCount} run{resubmittableCount === 1 ? '' : 's'} eligible for resubmit.
            </p>
          )}
        </div>
      )}
    </TileCard>
  );
}

function workflowNames(b: WorkflowRunsBreakdown | null): string[] {
  if (!b || b.status !== 'ok' || !b.rows) return [];
  // Sort: workflows with failures first (most likely to want actions on), then by volume.
  return [...b.rows]
    .sort((a, x) => x.failed - a.failed || x.total - a.total)
    .map((r) => r.workflow);
}

function pickDefaultWorkflow(b: WorkflowRunsBreakdown | null): string {
  const withFailures = b?.rows?.filter((r) => r.failed > 0) ?? [];
  if (withFailures.length > 0) {
    return [...withFailures].sort((a, x) => x.failed - a.failed)[0].workflow;
  }
  return b?.rows?.[0]?.workflow ?? '';
}
